import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  PageViewParamsType,
  trackCIOPageView,
  trackPageView
} from 'middleware/analytics/actions/trackPageView'
import {
  getAnonymousId,
  getCurrencyCode,
  getDeviceCategory,
  getLocaleCode,
  getUserCountryCode
} from 'modules/meta/selectors'
import {setAnalyticsPageName} from 'modules/meta/slice'
import {
  getHotel,
  getIsSearchCompleted,
  State
} from 'modules/sapiSearch/selectors'
import {Hotel} from 'modules/sapiSearch/slice'
import {getHotelId, getIsUserSearch} from 'modules/search/selectors'
import {getEpochSeconds, getTimezone} from 'utils'
import {getTrafficSource} from 'utils/trafficSource'

import {SIZES} from '@vio/atlas/Image'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {getIsLoading, selectUserId} from '@vio/core/src/auth/modules/selectors'

import {imageProvider} from '../../utils/imageProvider'

interface Props {
  pageName: Page
  trackingParams?: Partial<PageViewParamsType>
}

interface QueryParams {
  label?: string
  utm_source?: string
  checkIn?: string
  checkOut?: string
  hotelId?: string
  rooms?: string
}

const useTrackCIOPageView = ({pageName}) => {
  const dispatch = useDispatch()
  const {getQueryParams} = useRouterProvider()
  const isLoading = useSelector(getIsLoading)
  const id = useSelector(selectUserId)
  const currency = useSelector(getCurrencyCode)
  const locale = useSelector(getLocaleCode)
  const isUserSearch = useSelector(getIsUserSearch)
  const anonymousId = useSelector(getAnonymousId)
  const {tracking} = useBrandCapabilities()
  const stateHotelId = useSelector(getHotelId)
  const {
    checkIn,
    checkOut,
    hotelId: hotelIdFromSearch,
    rooms
  }: QueryParams = getQueryParams()

  const isSearchCompleted = useSelector(getIsSearchCompleted)
  const hotel = useSelector((state: State) =>
    getHotel(state, hotelIdFromSearch)
  ) as Hotel

  const {hotelName, displayAddress, imageURIs} = hotel || {}
  const hotelId = hotelIdFromSearch || stateHotelId

  const hotelImageUrl = imageURIs?.[0]
    ? imageProvider(imageURIs[0], SIZES['large'])
    : undefined

  useEffect(() => {
    if (!isLoading && tracking.hasCioTracking && isSearchCompleted)
      dispatch(
        trackCIOPageView(pageName, {
          id,
          currency,
          locale,
          checkIn: getEpochSeconds(checkIn),
          checkOut: getEpochSeconds(checkOut),
          pageUrl: location.href,
          isUserSearch,
          hotelId,
          anonymousId,
          rooms,
          hotelName,
          hotelAddress: displayAddress,
          hotelImageUrl,
          timezone: getTimezone()
        })
      )
  }, [isLoading, isSearchCompleted])
}

const TrackPageView = ({pageName, trackingParams}: Props) => {
  const dispatch = useDispatch()
  const {getQueryParams} = useRouterProvider()
  const deviceCategory = useSelector(getDeviceCategory)
  const userCountryCode = useSelector(getUserCountryCode)

  useTrackCIOPageView({pageName})

  useEffect(() => {
    const {label, utm_source: utmSource}: QueryParams = getQueryParams()
    const trafficSource = getTrafficSource(
      Array.isArray(utmSource) ? utmSource[0] : utmSource,
      Array.isArray(label) ? label[0] : label
    )
    const wasOpenedInBackground = document.hidden
    const initialVisibilityState = document.visibilityState

    dispatch(setAnalyticsPageName({page: pageName}))
    dispatch(
      trackPageView(pageName, {
        ...trackingParams,
        deviceCategory,
        userCountryCode,
        trafficSource
      })
    )

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.Page,
        action: Action.Displayed,
        page: pageName,
        payload: {
          ...trackingParams,
          wasOpenedInBackground,
          initialVisibilityState
        }
      })
    )
    // Make sure it only triggers once
  }, [])

  return null
}

export default TrackPageView
